import { useEffect } from 'react';
import shallow from 'zustand/shallow';
import debounce from 'lodash.debounce';
import { runningTradeConfig } from 'constants/datafeed';
import useDatafeedAuthEffect from './useDatafeedAuthEffect';
import { useRunningTradeStore } from './store/runningTrade';
import useDatafeedMessage from './useDatafeedMessage';
import useDatafeedSocket from './useDatafeedSocket';

const useRunningTrade = (
  key: RunningTradeKey,
  customFilter?: (items: RunningTradeItem[]) => RunningTradeItem[],
) => {
  const { setChannel, isAuthorized } = useDatafeedSocket();

  const addBatch = useRunningTradeStore((state) => state.addBatch);
  const shiftBatch = useRunningTradeStore((state) => state.shiftBatch);
  const setIsQueue = useRunningTradeStore((state) => state.setIsQueue);
  const setActive = useRunningTradeStore((state) => state.setActive);
  const setFilter = useRunningTradeStore((state) => state.setFilter);
  const clearData = useRunningTradeStore((state) => state.clearData);
  const play = useRunningTradeStore((state) => state.play);

  const runningTradeInstance = useRunningTradeStore(
    (state) => state.runningTrade[key],
  );
  const runningTradeAllFilter: Record<string, string> = useRunningTradeStore(
    (state) => {
      const dataReduced = Object.entries(state.runningTrade).reduce(
        (prev, [itemKey, itemValue]) => ({
          ...prev,
          ...(itemValue.filter !== '' ? { [itemKey]: itemValue.filter } : {}),
        }),
        { company: '', widget: '' },
      );

      if (
        dataReduced &&
        dataReduced.company &&
        dataReduced.widget &&
        dataReduced.widget === '*'
      ) {
        delete dataReduced.company;
      }

      return dataReduced;
    },
    shallow,
  );
  const { isQueue, isActive, isPaused } = useRunningTradeStore(
    (state) => ({
      isQueue: state.runningTrade[key].isQueue,
      isActive: state.runningTrade[key].isActive,
      isPaused: state.runningTrade[key].isPaused,
    }),
    shallow,
  );

  // getting non-reactive running trade instance
  const getRunningTradeInstance = () =>
    useRunningTradeStore.getState().runningTrade[key];

  const getStockByFilter = (newFilter?: string): string[] => {
    if (
      runningTradeAllFilter &&
      runningTradeAllFilter.company &&
      runningTradeAllFilter.widget &&
      runningTradeAllFilter.widget === '*'
    ) {
      delete runningTradeAllFilter.company;
      return Object.values({
        ...runningTradeAllFilter,
      });
    }

    return Object.values({
      ...runningTradeAllFilter,
      ...(newFilter !== undefined && { [key]: newFilter }),
    });
  };

  const setQueueInstanceBatch = () => {
    /** Recursive function not getting updated values,
     * with that we need to directly get value from store using getState() */
    const instance = getRunningTradeInstance();

    if (!instance.isQueue || !instance.isActive || instance.isPaused) return;

    setIsQueue(key);
    shiftBatch(key);
    setTimeout(
      () => setQueueInstanceBatch(),
      1000 / runningTradeConfig.widget.speed,
    );
  };

  /** Send empty string to stop running trade */
  const setInstanceFilter = (value: string) => {
    clearData(key);
    setFilter(key, value);
    setChannel({ runningTradeBatch: getStockByFilter(value) });
  };

  const setDebounceInstanceFilter = debounce(setInstanceFilter, 200);

  const setInstanceQueue = () => {
    setIsQueue(key);
  };

  const setInstanceActive = (value: boolean) => {
    setActive(key, value);
    setInstanceQueue();
    play(key);
  };

  const handleReceiveMessage = async (message: DatafeedMessageChannel) => {
    const instance = getRunningTradeInstance();
    if (!instance.isActive) return;
    const { runningTradeBatch, error } = message;
    const { batch = [] } = runningTradeBatch || {};

    if (error) throw error;

    let filteredBatch = batch;
    if (customFilter) {
      filteredBatch = customFilter(batch);
    }

    if (filteredBatch.length > 0) {
      addBatch(key, filteredBatch);
      if (instance.isActive && !instance.isQueue) setInstanceQueue();
    }
  };

  useDatafeedAuthEffect(() => {
    if (isActive) {
      setChannel({
        runningTradeBatch: Object.values(runningTradeAllFilter),
      });
    }
  }, [runningTradeAllFilter]);

  useDatafeedMessage('runningTradeBatch', key, handleReceiveMessage);

  useEffect(() => {
    if (isQueue || isPaused) setQueueInstanceBatch();
  }, [isQueue, isPaused]);

  return {
    runningTrade: runningTradeInstance,
    setInstanceActive,
    setInstanceFilter,
    setDebounceInstanceFilter,
    isLoading: !isAuthorized,
    runningTradeAllFilter,
  };
};

export default useRunningTrade;
